import React from 'react';
import PropTypes from 'prop-types';
import { offerLine } from '../../utils/offerPricing';

/**
 * The promotional-offer banner shown at the top of a pricelist. SHARED by the senior
 * (PackageChooser) and elementary (OptionSelectionContent) flows — one implementation.
 * `offer` is the coupon resolved by useOfferCoupon / for_landing_page.
 *
 * Headline: the admin-authored `promoHeadline` when set, otherwise a derived line from the
 * coupon ("20% off, $20 credit and free shipping. Use coupon FALL20"). This is where the
 * coupon CODE lives — cards show the discount (a struck-through % price, plus an order-level
 * "$X credit at checkout" line for the flat part) but not the code, so without this banner a
 * percentage coupon with no promoHeadline would strike a price with the code nowhere.
 * Renders nothing only when there's no offer AND nothing derivable.
 *
 * FULL-BLEED — spans the full viewport width (edge to edge) regardless of the centered
 * container it sits in, via the `w-screen` + `margin-left: calc(50% - 50vw)` breakout.
 * STICKY — it scrolls up WITH the packages until it reaches the top of the viewport, then
 * pins there (`sticky top-0`) while the packages keep scrolling up and off underneath it
 * (`z-10` keeps the banner above the cards). The customer nav is in-flow (not fixed), so
 * `top-0` pins to the true viewport top after the nav scrolls away; the app shell uses
 * `overflow-x: clip` (not `hidden`) specifically so no ancestor traps this sticky element.
 * `className` is just for per-caller spacing (e.g. `mt-2`).
 */
export default function PromoBanner({ offer, className }) {
  if (!offer) return null;
  const headline = offer.promoHeadline || offerLine(offer);
  if (!headline) return null;

  return (
    <div
      className={`sticky top-0 z-10 w-screen ml-[calc(50%_-_50vw)] px-5 py-4 text-center shadow-md ${className}`}
      style={{ background: 'linear-gradient(90deg, #fde68a, #f7b72d)' }}
    >
      <p className="text-lg sm:text-xl font-extrabold text-gray-900">{headline}</p>
      {offer.promoSubhead && (
        <p className="mt-0.5 text-sm font-medium text-gray-800">{offer.promoSubhead}</p>
      )}
    </div>
  );
}

PromoBanner.defaultProps = { offer: null, className: 'mt-2' };

PromoBanner.propTypes = {
  offer: PropTypes.shape({
    promoHeadline: PropTypes.string,
    promoSubhead: PropTypes.string,
    // Used to derive a headline when promoHeadline is absent (offerLine).
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flatValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
  }),
  // Per-flow width + corner-radius so the banner matches that flow's cards.
  className: PropTypes.string,
};
